import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { KeyRound, Loader2, LogOut, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import BookingCard from "../components/admin/BookingCard";
import ChangePasswordForm from "../components/admin/ChangePasswordForm";

const FILTERS = ["all", "new", "confirmed", "done", "cancelled"];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState(null);
  const [filter, setFilter] = useState("all");
  const [showPw, setShowPw] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/bookings");
      setBookings(data);
    } catch {
      navigate("/admin/login");
    }
  }, [navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const setStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/bookings/${id}`, { status });
      setBookings((list) => list.map((b) => (b.id === id ? data : b)));
      toast.success(`Marked as ${status}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const logout = async () => {
    await api.post("/auth/logout");
    navigate("/admin/login");
  };

  if (!bookings) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink">
        <Loader2 className="h-8 w-8 animate-spin text-gold" />
      </div>
    );
  }

  const shown = filter === "all" ? bookings : bookings.filter((b) => b.status === filter);
  const count = (s) => (s === "all" ? bookings.length : bookings.filter((b) => b.status === s).length);

  return (
    <div className="min-h-screen bg-ink text-white">
      <header className="sticky top-0 z-30 border-b border-white/5 bg-[#0b0b0b]/95 backdrop-blur">
        <div className="mx-auto flex h-[70px] max-w-5xl items-center justify-between px-5">
          <img src="/assets/singh-taxi-logo.png" alt="Singh Taxi" className="h-10 w-auto rounded-md" />
          <div className="flex items-center gap-2">
            <IconBtn testid="admin-refresh-button" onClick={load} label="Refresh"><RefreshCw className="h-4 w-4" /></IconBtn>
            <IconBtn testid="admin-password-button" onClick={() => setShowPw((v) => !v)} label="Change password"><KeyRound className="h-4 w-4" /></IconBtn>
            <IconBtn testid="admin-logout-button" onClick={logout} label="Log out"><LogOut className="h-4 w-4" /></IconBtn>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-5 py-10">
        {showPw && <ChangePasswordForm onDone={() => setShowPw(false)} />}
        <div className="flex items-center gap-3">
          <span className="h-px w-8 bg-gold/80" />
          <span className="text-xs font-bold tracking-[0.3em] text-gold">BOOKINGS</span>
        </div>
        <h1 data-testid="admin-heading" className="mt-4 font-serif text-4xl sm:text-5xl">Ride requests</h1>

        <div className="mt-8 flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button
              key={f}
              data-testid={`admin-filter-${f}`}
              onClick={() => setFilter(f)}
              className={`rounded-full px-4 py-2 text-xs font-extrabold uppercase tracking-wider transition-colors ${
                filter === f ? "bg-gold text-black" : "border border-white/10 text-white/70 hover:text-white"
              }`}
            >
              {f} <span className="opacity-60">({count(f)})</span>
            </button>
          ))}
        </div>

        <div data-testid="admin-bookings-list" className="mt-8 space-y-4">
          {shown.length === 0 && (
            <p data-testid="admin-empty" className="rounded-2xl border border-white/5 bg-coal p-10 text-center text-white/50">
              No bookings yet.
            </p>
          )}
          {shown.map((b) => (
            <BookingCard key={b.id} booking={b} onStatus={setStatus} />
          ))}
        </div>
      </main>
    </div>
  );
}

function IconBtn({ testid, onClick, label, children }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      aria-label={label}
      title={label}
      className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 text-white/80 transition-colors hover:border-gold/40 hover:text-gold"
    >
      {children}
    </button>
  );
}
